/** @format */

import {useEffect, useState} from 'react';
import PropTypes from 'prop-types';

import {useOptionallyControlledState} from '../hooks/use_optionally_controlled_state';
import {IconButton, Icon, Tooltip} from '@sttark/design-system';

const autoRatios = {
    1: '21x9',
    2: '16x9',
    3: '4x3',
    4: '4x3',
    default: '1x1'
};

export const GridSelect = ({
    value: controlledValue,
    onChange,
    options,
    ratio,
    minCols,
    maxCols,
    mobileCols,
    multiple,
    maxDescriptionLength = 80,
    includeAccessibilityIcon,
    ...props
}) => {
    // To take up all horizontal space, the number of columns is the number of options
    // provided, or maxCols if the number of options exceeds it, at which point the grid will
    // wrap.
    const numCols = Math.min(Math.max(options.length, minCols), maxCols);
    const rowClass = `grid-select row row-cols-${mobileCols} row-cols-sm-${numCols}`;

    if (ratio === 'auto') {
        ratio = autoRatios[numCols] || autoRatios.default;
    }

    const [value, setValue] = useOptionallyControlledState(controlledValue, undefined, onChange);

    // Handle capping a maximum number of selections, if specified by the multiple prop.
    const capMultiple = arr => (typeof multiple === 'number' ? arr.slice(0, multiple) : arr);

    useEffect(() => {
        options.forEach(o => {
            if (o.disabled && isSelected(o.value)) handleClick(o.value);
        });
    }, [options]);

    // TODO: isSelected and handleClick support only scalar values. If objects/arrays need
    // to be supported as option values, need to use a deep equality library or
    // JSON.stringify hackery.

    const isSelected = v => (Array.isArray(value) ? value.includes(v) : v == value);
    const handleClick = v => {
        if (multiple) {
            const arr = value || [];
            setValue(isSelected(v) ? arr.filter(item => item != v) : capMultiple(arr.concat(v)));
        } else {
            if (!isSelected(v)) {
                setValue(v);
            }
        }
    };

    // Handle casting value to an array as needed if the multiple prop changes
    useEffect(() => {
        if (value == null) return;
        if (multiple) {
            setValue(capMultiple(Array.isArray(value) ? value : [value]));
        } else {
            setValue(Array.isArray(value) ? value[0] : value);
        }
    }, [multiple]);

    return (
        <div className={rowClass} {...props}>
            {options.map(o => {
                return (
                    <div key={o.value} className="col mb-5">
                        {typeof props.children === 'function' ? (
                            props.children({
                                option: o,
                                selected: isSelected(o.value),
                                handleClick,
                                // value and setValue are provided for full control over value
                                // selection for advanced scenarios
                                value,
                                setValue
                            })
                        ) : (
                            <>
                                <button
                                    className={`grid-select--button ratio ratio-${ratio} border-all`}
                                    aria-pressed={isSelected(o.value)}
                                    onClick={() => handleClick(o.value)}
                                    disabled={!!o.disabled}>
                                    <span className="d-flex p-1 align-items-center justify-content-center">
                                        {o.image &&
                                            (typeof o.image === 'function' ? (
                                                o.image(o)
                                            ) : (
                                                <img
                                                    alt=""
                                                    src={o.image}
                                                    onMouseEnter={e => {
                                                        o.hoverImage &&
                                                            (e.currentTarget.src = o.hoverImage);
                                                    }}
                                                    onMouseOut={e => {
                                                        o.hoverImage &&
                                                            (e.currentTarget.src = o.image || '');
                                                    }}
                                                    style={{maxWidth: '100%', maxHeight: '100%'}}
                                                />
                                            ))}
                                    </span>
                                    {isSelected(o.value) && includeAccessibilityIcon && (
                                        <span className="grid-select--button--selected">
                                            <Icon name="Check" medium />
                                        </span>
                                    )}
                                </button>
                                {!o?.description && (
                                    <div className="font-body-sm d-flex align-items-start">
                                        <span
                                            className={`flex-grow-1 ${
                                                o.disabled ? 'opacity-25' : ''
                                            }`}>
                                            {o.label}
                                        </span>
                                        {o.info &&
                                            (typeof o.info === 'function' ? (
                                                o.info(o)
                                            ) : (
                                                <Tooltip content={o.info}>
                                                    <IconButton
                                                        secondary
                                                        tabIndex="-1"
                                                        icon="Info"
                                                    />
                                                </Tooltip>
                                            ))}
                                    </div>
                                )}
                                {o?.description && typeof o.description === 'string' && (
                                    <div className="grid-select-description mx-0 p-4 bg-structure-lightest">
                                        <h4 className="m-0 p-0 font-heading-sm">{o.label}</h4>
                                        <p className="m-0 p-0">
                                            {o.description.length >= maxDescriptionLength ? (
                                                <>
                                                    {o.description.substring(
                                                        0,
                                                        maxDescriptionLength
                                                    )}
                                                    ...{' '}
                                                    <button
                                                        className="p-0 m-0 grid-select-description_btn"
                                                        // onClick={() =>
                                                        //     setIsShowingEntireDescription(true)
                                                        // }
                                                    >
                                                        <b className="color-action-primary">
                                                            View More
                                                        </b>
                                                    </button>
                                                </>
                                            ) : (
                                                o.description
                                            )}
                                        </p>
                                    </div>
                                )}
                                {o?.description &&
                                    typeof o.description !== 'string' &&
                                    o.description}
                            </>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default GridSelect;

GridSelect.propTypes = {
    value: PropTypes.any,
    onChange: PropTypes.func,
    maxDescriptionLength: PropTypes.number,
    /** Enable multiple selection. This prop can optionally specify the maximum number of
     * items selectable. */
    multiple: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    options: PropTypes.arrayOf(
        PropTypes.shape({
            description: PropTypes.any,
            label: PropTypes.string.isRequired,
            /** The value of the option, if selected. Only supports scalar values. */
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            /** The option's info may be a string, which will display as a hoverable
             * tooltip, or a custom render function. */
            info: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
            /** The image to display. May be the src of an image, or a custom render
             * function. */
            image: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
            hoverImage: PropTypes.any,
            disabled: PropTypes.bool
        })
    ).isRequired,
    ratio: PropTypes.oneOf(['auto', '1x1', '4x3', '16x9', '21x9']),
    minCols: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    maxCols: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    mobileCols: PropTypes.oneOf([1, 2, 3, 4, 5, 6]),
    includeAccessibilityIcon: PropTypes.bool,
    /** A custom render prop can be provided for complete control over the GridSelect
     * items while maintaining the grid layout. */
    children: PropTypes.func
};

GridSelect.defaultProps = {
    options: [],
    ratio: 'auto',
    minCols: 1,
    maxCols: 5,
    mobileCols: 2,
    multiple: false,
    includeAccessibilityIcon: true
};
